import { useMemo, useState } from "react";
import { useHistory, useRouteMatch } from "react-router-dom";
import {
  createColumnHelper,
  getCoreRowModel,
  Row,
  useReactTable,
} from "react-table-8.10.7";
import { TableUI } from "components/design/TableUI";
import DashboardContainer from "components/dashboard/DashboardContainer";
import Async from "components/DesignSystem/AsyncComponents/Async";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { DashboardLayout } from "components/DashboardLayout";
import { Header } from "components/DesignSystem/Header/Header";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { Button } from "components/DesignSystem/Button/Button";
import Wind from "components/icons/Wind";
import { AddRegistrationDropdown } from "./AddRegistrationDropdown";
import classNames from "classnames";
import { useGetCountriesDataQuery } from "store/apis/onboarding";
import { useQuery, useUpdateQuery } from "hooks/useQuery";
import { AddRegistrationModal } from "./AddRegistrationModal";
import { FilingStatusBadge } from "./FilingStatusBadge";
import { useInitiateSalesTaxRegistrationMutation } from "store/apis/salesTaxRegistrationDetails";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { getBillingCartUrl } from "util/getBillingCartUrl";
import { BackendError } from "types/utils/error";
import { NudgeAddFilingToCartModal } from "components/SalesTax/NudgeAddFilingToCartModal";
import { AddExistingRegistrationModal } from "./AddExistingRegistrationModal";
import ConditionalToolTip from "components/design/conditionalToolTip";
import {
  Registration,
  RegistrationStatus,
  useGetSalesTaxRegistrationsQuery,
} from "store/apis/salesTaxRegistration";
import dayjs from "dayjs";
import { DISPLAY_DATE } from "constants/date";
import { RegistrationDetails } from "./RegistrationDetails";
import { MobileRegistrationRow } from "./MobileRegistrationRow";
import { useWindowSize } from "hooks/useWindowSize";
import { useDispatch } from "react-redux";
import { BusinessDetailsModal } from "./BusinessDetailsModal";
import { useToast } from "hooks/useToast";
import {
  ANNUALLY,
  FILING_IN_PROGRESS,
  FILING_INFO_MISSING,
  IN_REVIEW,
  MONTHLY,
  PENDING_DETAILS,
  PROCESSING,
  QUARTERLY,
  REGISTERED,
  SEMI_ANNUALLY,
  VALIDATING,
} from "constants/salesTaxRegistrationStatusType";
import { SalesTaxRegisrationSlider } from "./SalesTaxRegistrationSlider";
import { useModal } from "hooks/useModal";
import { closeFloatingChat, setToOpenChatId } from "store/slices/chat";
import OutsideClickHandler from "react-outside-click-handler";
import { FloatingChat } from "components/floatingChat/FloatingChat";
import { useCurrentEntity } from "hooks/useCurrentEntity";
import { useGetNexusTrackerSummaryQuery } from "store/apis/salesTax";
import { RegistrationsOverviewCard } from "./RegistrationsOverviewCard";
import { NotRegisteredAlertCard } from "./NotRegisteredAlertCard";

export type initialRegistrationDataType = {
  state_registration_id: string;
  state: string;
  state_id: string;
  status?: string;
};

const columnHelper = createColumnHelper<Registration>();

export const getSelectedState = (
  stateId: string,
  states?: { label: string; value: string }[]
) => {
  const selectedState = states?.find((state) => state.value === stateId);
  return selectedState ? selectedState.label : "";
};

export const paymentScheduleOption = [
  { label: "Monthly", value: MONTHLY },
  { label: "Quarterly", value: QUARTERLY },
  { label: "Annually", value: ANNUALLY },
  { label: "Semi-Annually", value: SEMI_ANNUALLY },
];

export const StatusMap = {
  PENDING_DETAILS: "Not started",
  IN_REVIEW: "In review",
  PROCESSING: "Processing",
  REGISTERED: "Registered",
  VALIDATING: "In cart",
  FILING_IN_PROGRESS: "Filing in progress",
  NOT_STARTED: "Not started",
  FILING_INFO_MISSING: "Filing info missing",
};

const StatusBadge = ({ status }: { status: RegistrationStatus }) => {
  return (
    <div
      className={classNames(
        "t-text-text-60 t-max-w-fit t-text-body-sm t-text-center t-px-2 t-py-1 t-text-nowrap t-rounded-md",
        {
          "t-bg-red-10 ": status === PENDING_DETAILS,
        },
        {
          "t-bg-blue-10 t-text-body-sm":
            status === IN_REVIEW ||
            status === PROCESSING ||
            status === VALIDATING ||
            status === FILING_IN_PROGRESS,
        },
        {
          "t-bg-green-20": status === REGISTERED,
        },
        {
          "t-bg-orange-10 !t-text-orange-80": status === FILING_INFO_MISSING,
        }
      )}
    >
      <span>{StatusMap[status]}</span>
    </div>
  );
};

const getToolTipContent = (status: RegistrationStatus) => {
  switch (status) {
    case PENDING_DETAILS:
      return (
        <p className="t-m-0">
          Please provide required details to initiate registration
        </p>
      );
    case IN_REVIEW:
      return (
        <p className="t-m-0">
          Reviewing your info before <br /> starting registration
        </p>
      );
    case PROCESSING:
      return (
        <p className="t-m-0">
          Application submitted! License arrives <br /> at your mailing address
          in 1-2 weeks
        </p>
      );
    case REGISTERED:
      return (
        <p className="t-m-0">
          Completed! <br />
          You’re ready to file
        </p>
      );
    case VALIDATING:
      return (
        <p className="t-m-0">
          Added to your cart. Complete <br />
          payment to start processing
        </p>
      );
    case FILING_IN_PROGRESS:
      return (
        <p className="t-m-0">
          Payment received, our team <br />
          is actively working on this filing
        </p>
      );
    case FILING_INFO_MISSING:
      return (
        <p className="t-m-0">
          Registered! Add filing details <br />
          so we can start filing for you
        </p>
      );
    default:
      return <p></p>;
  }
};

export const SalesTaxRegistrations = () => {
  const [initialRegistrationData, setInitialRegistrationData] =
    useState<initialRegistrationDataType>({
      state_registration_id: "",
      state: "",
      state_id: "",
      status: "",
    });

  const [selectedRegistrationId, setSelectedRegistrationId] = useState("");
  const entityId = useCurrentEntityId();
  const { uuid: groupId } = useCurrentGroupContext();
  const { isAdmin } = useRoleBasedView();
  const query = useQuery();
  const { update } = useUpdateQuery();
  const { push, location } = useHistory();
  const { url } = useRouteMatch();
  const nexusTrackerUrl = url.replace(/\/registrations\/?$/, "");

  const handleViewNexusTracker = () => {
    push(`${nexusTrackerUrl}${location.search}`);
  };

  const { data: countries } = useGetCountriesDataQuery();
  const { alertToast, info: infoToast } = useToast();
  const dispatch = useDispatch();
  const [initiatingRegistrationId, setInitiatingRegistrationId] = useState("");
  const [initiateRegistration, { isLoading: isInitiatingRegistration }] =
    useInitiateSalesTaxRegistrationMutation();

  const stateId = query.get("state_id");
  const type = query.get("type");
  const newRegistartionModal = useModal();
  const existingRegistrationModal = useModal();
  const businessDetailsModal = useModal();
  const nudgeAddFilingModal = useModal();
  const [nudgeRegistration, setNudgeRegistration] = useState({
    stateId: "",
    stateName: "",
  });

  const handleRegistered = (
    registeredStateId: string,
    registeredStateName: string
  ) => {
    setNudgeRegistration({
      stateId: registeredStateId,
      stateName: registeredStateName,
    });
    nudgeAddFilingModal.open();
  };
  const detailsModal = useModal();
  const registionationTypeModal = useModal();
  const salesTaxRegistrationSlider = useModal();

  const {
    data: registrations,
    isLoading,
    isSuccess,
  } = useGetSalesTaxRegistrationsQuery({
    entityId,
  });

  const { data: nexusTrackerSummary } = useGetNexusTrackerSummaryQuery(
    { entityId },
    { skip: !entityId }
  );

  const unitatedStates = countries?.find(
    (country) => country.name === "United States"
  )?.states;

  const stateOptions = unitatedStates
    ?.filter((state) => {
      return !registrations?.some(
        (registration) => registration.state_id == state.uuid
      );
    })
    .map((state) => ({
      label: state.name,
      value: state.uuid,
    }));

  const handleRegistrationFromQuery = () => {
    if (registionationTypeModal.isOpen && !type) {
      return;
    }
    const registeredState = registrations?.find(
      (registration) => registration.state_id === stateId
    );
    if (registeredState && registeredState.status !== PENDING_DETAILS) {
      handleClick(registeredState);
      return;
    }
    const selectedState = unitatedStates?.find(
      (state) => state.uuid === stateId
    );
    if (!selectedState) {
      return;
    }
    setInitialRegistrationData({
      state: selectedState.name,
      state_id: stateId || "",
      state_registration_id: registeredState?.state_registration_id || "",
    });

    if (type === "new") {
      newRegistartionModal.open();
    } else if (type === "existing") {
      existingRegistrationModal.open();
    }
    registionationTypeModal.close();
  };
  const handleRegistrationModalClose = (
    type: "new" | "existing" | "type" | "details"
  ) => {
    setInitialRegistrationData({
      state_registration_id: "",
      state: "",
      state_id: "",
      status: "",
    });
    update({
      query: "state_id",
      value: null,
    });
    update({
      query: "type",
      value: null,
    });

    registionationTypeModal.close();

    switch (type) {
      case "new":
        return newRegistartionModal.close();
      case "existing":
        return existingRegistrationModal.close();
      case "type":
        update({
          query: "state_id",
          value: null,
        });
        update({
          query: "type",
          value: null,
        });
        registionationTypeModal.close();
        return;
      case "details":
        update({
          query: "state_registration_id",
          value: null,
        });
        detailsModal.close();
        return;
      default:
        return;
    }
  };

  const handleRowClick = (rowData: Registration) => {
    setSelectedRegistrationId(rowData.state_registration_id);
    salesTaxRegistrationSlider.open();
  };

  const handleInitiateRegistration = async (rowData: Registration) => {
    setInitiatingRegistrationId(rowData.state_registration_id);
    try {
      await initiateRegistration({
        entityId,
        registrationId: rowData.state_registration_id,
      }).unwrap();
      const cartUrl = getBillingCartUrl({ isAdmin, groupId, entityId });
      window.open(cartUrl, "_blank");
    } catch (error) {
      alertToast({ message: (error as BackendError).data?.error?.message });
    } finally {
      setInitiatingRegistrationId("");
    }
  };

  const handleViewFiling = (rowData: Registration) => {
    if (!rowData.launched_task_id) return;
    push(`/tax/filings/${rowData.launched_task_id}`);
  };

  const handleClick = (rowData: Registration) => {
    salesTaxRegistrationSlider.close();
    const status = rowData.status;

    if (status === PENDING_DETAILS) {
      // Already has a real state + registration row — add it straight to
      // cart, no intake form (the same details get collected later in Filings).
      void handleInitiateRegistration(rowData);
    }
    if (status === REGISTERED && !isAdmin) {
      update({
        query: "state_registration_id",
        value: rowData.state_registration_id,
      });
      detailsModal.open();
    }

    if (status === PROCESSING || status === FILING_INFO_MISSING) {
      setInitialRegistrationData({
        state_registration_id: rowData.state_registration_id,
        state: rowData.state,
        state_id: rowData.state_id,
        status: rowData.status,
      });
      existingRegistrationModal.open();
    }

    if (status === FILING_IN_PROGRESS) {
      handleViewFiling(rowData);
    }

    if (status === VALIDATING) {
      const cartUrl = getBillingCartUrl({ isAdmin, groupId, entityId });
      window.open(cartUrl, "_blank");
    }

    if (status === IN_REVIEW || (status === REGISTERED && isAdmin)) {
      if (isAdmin) {
        setInitialRegistrationData({
          state_registration_id: rowData.state_registration_id,
          state: rowData.state,
          state_id: rowData.state_id,
          status: rowData.status,
        });
        existingRegistrationModal.open();
        return;
      }
      infoToast({
        title: "Registration in review",
        message: "Reviewing your info before starting registration.",
      });
    }
  };

  const columns = useMemo(
    () => [
      columnHelper.accessor("state", {
        header: "State",
        cell: (info) => info.getValue(),
        size: 30,
      }),
      columnHelper.accessor("request_date", {
        header: "Requested on",
        cell: (info) =>
          info.getValue() ? dayjs(info.getValue()).format(DISPLAY_DATE) : "-",
        size: 30,
      }),
      columnHelper.accessor("status", {
        header: "Registration Status",
        cell: (info) => {
          const value = info.getValue();
          const launchedTaskId = info.row.original.launched_task_id;

          if (value === FILING_IN_PROGRESS && launchedTaskId) {
            return <FilingStatusBadge taskId={launchedTaskId} />;
          }

          return (
            <span className="t-text-body-sm ">
              <ConditionalToolTip
                side="left"
                text={getToolTipContent(value as RegistrationStatus)}
                condition={value}
              >
                <div>{value ? <StatusBadge status={value} /> : "-"}</div>
              </ConditionalToolTip>
            </span>
          );
        },
        size: 30,
      }),
      columnHelper.accessor("tax_detail_id", {
        header: "",
        cell: (info) => {
          const status = info.row.original.status;
          const isRowInitiating =
            isInitiatingRegistration &&
            initiatingRegistrationId ===
              info.row.original.state_registration_id;

          const isNonAdminSimpleView =
            (status === IN_REVIEW ||
              status === REGISTERED ||
              status === VALIDATING) &&
            !isAdmin;

          let actionLabel = "";
          if (isNonAdminSimpleView) {
            actionLabel = "View";
          } else {
            if (status === PENDING_DETAILS) actionLabel = "Initiate";
            if (status === PROCESSING) actionLabel = "Add info";
            if (status === FILING_IN_PROGRESS) actionLabel = "View filing";
            if (status === IN_REVIEW) actionLabel = "Review";
            if (status === REGISTERED) actionLabel = "View";
            if (status === VALIDATING) actionLabel = "View cart";
            if (status === FILING_INFO_MISSING)
              actionLabel = "Add filing details";
          }

          const actionButtonType =
            status === PENDING_DETAILS ? "primary" : "secondary";

          const handleActionClick = (
            e: React.MouseEvent<HTMLButtonElement>
          ) => {
            e.stopPropagation();
            handleClick(info.row.original);
          };

          return (
            <div
              className={classNames("t-w-full t-flex", {
                "t-justify-center": !isAdmin,
                "t-justify-start": isAdmin,
              })}
            >
              <Button
                size="small"
                customType={actionButtonType}
                isLoading={isRowInitiating}
                disabled={isRowInitiating}
                onClick={handleActionClick}
              >
                {actionLabel}
              </Button>
            </div>
          );
        },
        size: 10,
      }),
    ],
    []
  );

  const { isMobile } = useWindowSize();

  const table = useReactTable({
    data: registrations ? registrations : [],
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const handleTableRowClick = (row: Row<Registration>) =>
    handleRowClick(row.original);

  const registrationsList = isMobile ? (
    <div className="t-flex t-flex-col t-gap-3">
      {table.getRowModel().rows.map((row) => (
        <MobileRegistrationRow
          key={row.id}
          row={row}
          onClick={handleRowClick}
        />
      ))}
    </div>
  ) : (
    <TableUI
      sticky={false}
      onRowClick={handleTableRowClick}
      table={table}
      size="regular"
      isHeaderCellUpperCase={false}
    />
  );

  useMemo(() => {
    if (isSuccess && stateId) {
      handleRegistrationFromQuery();
    }
  }, [stateId, type, isSuccess, registrations, unitatedStates]);

  const { salestax_channel } = useCurrentEntity();

  useMemo(() => {
    if (salestax_channel) {
      dispatch(setToOpenChatId(salestax_channel));
    }
  }, [salestax_channel]);
  return (
    <DashboardLayout
      header={
        <Header
          v2
          title="Registrations"
          right={
            <div className="t-flex gap-0">
              {/* <Button
                size="small"
                customType="secondary"
                onClick={businessDetailsModal.open}
              >
                Update details
              </Button> */}
              <AddRegistrationDropdown
                onAddNew={newRegistartionModal.open}
                onAddExisting={existingRegistrationModal.open}
              />
            </div>
          }
        />
      }
    >
      <BusinessDetailsModal
        open={businessDetailsModal.isOpen}
        onClose={businessDetailsModal.close}
      />
      <AddRegistrationModal
        open={newRegistartionModal.isOpen}
        onClose={() => handleRegistrationModalClose("new")}
        stateOptions={stateOptions}
      />
      {existingRegistrationModal.isOpen && (
        <AddExistingRegistrationModal
          initialRegistrationData={initialRegistrationData}
          onClose={() => handleRegistrationModalClose("existing")}
          onRegistered={handleRegistered}
        />
      )}
      <NudgeAddFilingToCartModal
        isOpen={nudgeAddFilingModal.isOpen}
        onClose={nudgeAddFilingModal.close}
        entityId={entityId}
        groupId={groupId}
        stateId={nudgeRegistration.stateId}
        stateName={nudgeRegistration.stateName}
      />
      <RegistrationDetails
        open={detailsModal.isOpen}
        onClose={() => handleRegistrationModalClose("details")}
      />

      <SalesTaxRegisrationSlider
        isOpen={salesTaxRegistrationSlider.isOpen}
        onClose={salesTaxRegistrationSlider.close}
        selectedRegistrationId={selectedRegistrationId}
        handleClick={handleClick}
      />

      <Async.Root
        isLoading={isLoading}
        isEmpty={Boolean(!registrations?.length)}
        isSuccess={isSuccess}
      >
        <Async.Empty>
          <div className="t-flex t-flex-col t-items-center t-mt-40">
            <Wind className="t-text-text-30" />
            <p className="t-text-text-60 t-text-body t-font-medium t-mb-0 t-mt-4">
              No registrations needed yet
            </p>
            <p className="t-text-text-30 t-text-body-sm t-mb-0 t-mt-1 t-text-center t-max-w-[450px]">
              You haven&apos;t crossed a threshold or declared a physical
              presence in any state, so there&apos;s nothing to register for.
              States appear here automatically the moment one is triggered.
            </p>
            <div className="t-flex t-items-center t-gap-2 t-mt-4">
              <AddRegistrationDropdown
                onAddNew={newRegistartionModal.open}
                onAddExisting={existingRegistrationModal.open}
              />
              <Button
                size="small"
                customType="secondary"
                onClick={handleViewNexusTracker}
              >
                View Nexus tracker
              </Button>
            </div>
          </div>
        </Async.Empty>
        <Async.Success>
          <DashboardContainer>
            <DashboardContainer.Content>
              <div className="t-flex t-gap-4 t-pt-4 sm:t-flex-col">
                <RegistrationsOverviewCard
                  registrations={registrations || []}
                />
                {Boolean(nexusTrackerSummary?.exposed_not_registered_count) && (
                  <NotRegisteredAlertCard
                    count={nexusTrackerSummary!.exposed_not_registered_count}
                    nexusTrackerUrl={`${nexusTrackerUrl}${location.search}`}
                  />
                )}
              </div>
              <div className="t-pt-4">{registrationsList}</div>
            </DashboardContainer.Content>
          </DashboardContainer>
        </Async.Success>
      </Async.Root>
      {salestax_channel && (
        <OutsideClickHandler
          onOutsideClick={() => {
            dispatch(closeFloatingChat());
          }}
        >
          <FloatingChat />
        </OutsideClickHandler>
      )}
    </DashboardLayout>
  );
};
